import CustomButton from "@/components/common/Button";
import { filterByLabel } from "@/lib/filterByLabel";
import { DropdownService, TenantPermissionService } from "@/services/APIs";
import { useA1ResolvedNav } from "@/hooks/useA1ResolvedNav";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Select } from "antd";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PermissionModal, PermissionsTable } from "./components";
import { t } from "i18next";
import "./permissionPage.css";

const PermissionPage = () => {
  const navigationRoutes = useA1ResolvedNav();
  const [searchParams] = useSearchParams();
  const role = searchParams.get("role");
  const schema = searchParams.get("schema");
  const [selectedRole, setSelectedRole] = useState<string | null>(role ?? null);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(
    schema ?? null
  );
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const { data: roleOptions, isLoading: roleLoading } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  const { data: schemaOptions, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  const handleCancel = () => {
    setOpen(false);
  };

  const toolBarRender = () => {
    return [
      <CustomButton
        key="add"
        type="primary"
        onClick={() => setOpen(true)}
        title="Add Permission"
        className="permission-list-add"
      >
        <Plus size={16} className="mr-1" /> {t("Add Permission")}
      </CustomButton>,
    ];
  };

  const {
    data: permissions,
    isLoading: permissionsLoading,
    refetch: refetchPermissions,
  } = useQuery<{
    results: any[];
  }>({
    queryKey: ["permissions", selectedRole, selectedSchema, page, pageSize],
    queryFn: async () =>
      TenantPermissionService.getPermissionByRoleId(
        selectedRole ?? "",
        selectedSchema ?? "",
        page,
        pageSize
      ),
    enabled: !!selectedRole && !!selectedSchema,
    retry: false,
  });

  return (
    <div className="permission-page min-w-0 p-4 md:p-0">
      <Card
      title={
        <PageHeader
          className="custom-page-header"
          title="Permission"
          onBack={() => navigate(navigationRoutes.USERS)}
        />
      }
    >
      <div className="permission-filters mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Select
          className="w-full"
          options={roleOptions}
          placeholder="Select Role"
          onChange={value => {
            setSelectedRole(value);
          }}
          value={selectedRole}
          showSearch
          allowClear
          loading={roleLoading}
          filterOption={filterByLabel}
        />
        <Select
          className="w-full"
          options={schemaOptions?.map((schema: any) => ({
            value: schema.label,
            label: schema.label,
          }))}
          placeholder="Select Schema"
          onChange={value => {
            setSelectedSchema(value);
          }}
          showSearch
          value={selectedSchema}
          allowClear
          loading={schemaLoading}
          filterOption={filterByLabel}
        />
      </div>

      <PermissionsTable
        permissions={permissions}
        permissionsLoading={permissionsLoading}
        roleOptions={roleOptions ?? []}
        toolBarRender={toolBarRender}
        schemaOptions={schemaOptions ?? []}
        refetchPermissions={refetchPermissions}
        page={page}
        pageSize={pageSize}
        onPageChange={(page, pageSize) => {
          setPage(page);
          setPageSize(pageSize ?? 10);
          searchParams.set("page", page.toString());
          searchParams.set("pageSize", pageSize.toString());
          navigate(
            `${navigationRoutes.PERMISSIONS}?${searchParams.toString()}`
          );
        }}
      />

      <PermissionModal
        open={open}
        onCancel={handleCancel}
        mode="create"
        refetchPermissions={refetchPermissions}
        selectedSchema={selectedSchema ?? ""}
        selectedRole={selectedRole ?? ""}
      />
    </Card>
    </div>
  );
};

export default PermissionPage;
